import { useEffect, useMemo, useState } from 'react'
import { Empty, Spin, Tree, Typography } from 'antd'
import { FileOutlined, FolderOutlined } from '@ant-design/icons'
import type { DataNode } from 'antd/es/tree'
import { adminApi } from '../../api'
import type { KnowledgeScope } from '../../constants/knowledgeScope'
import './CategoryBrowsePanel.css'

export type DocCategory = {
  id: number
  parent_id?: number | null
  name: string
  path?: string
  is_leaf?: boolean
  description?: string
  preset_tags?: string
  sort_order: number
  is_active: boolean
  children?: DocCategory[]
}

type Props = {
  scope: KnowledgeScope
  value?: string
  onChange?: (path: string) => void
}

function flattenCategories(nodes: DocCategory[]): DocCategory[] {
  const out: DocCategory[] = []
  const walk = (list: DocCategory[]) => {
    for (const node of list) {
      out.push(node)
      if (node.children?.length) walk(node.children)
    }
  }
  walk(nodes)
  return out
}

function findCategoryByPath(nodes: DocCategory[], path: string): DocCategory | undefined {
  for (const node of flattenCategories(nodes)) {
    if ((node.path || node.name) === path) return node
  }
  return undefined
}

function toTreeData(nodes: DocCategory[]): DataNode[] {
  return nodes.map((node) => {
    const path = node.path || node.name
    const isLeaf = node.is_leaf ?? !node.children?.length
    return {
      key: node.id,
      title: isLeaf ? (
        <span className="app-browse-category-tree-leaf" title={path}>
          {node.name}
        </span>
      ) : (
        <span className="app-browse-category-tree-group" title={path}>
          {node.name}
        </span>
      ),
      icon: isLeaf ? <FileOutlined /> : <FolderOutlined />,
      disabled: !isLeaf,
      children: node.children?.length ? toTreeData(node.children) : undefined,
    }
  })
}

export function CategoryBrowsePanel({ scope, value, onChange }: Props) {
  const [loading, setLoading] = useState(false)
  const [tree, setTree] = useState<DocCategory[]>([])

  useEffect(() => {
    setLoading(true)
    adminApi
      .get<{ items: DocCategory[] }>('/admin/knowledge/doc-categories', {
        params: { active_only: true, tree: true },
      })
      .then((r) => setTree(r.data.items || []))
      .catch(() => setTree([]))
      .finally(() => setLoading(false))
  }, [scope])

  const treeData = useMemo(() => toTreeData(tree), [tree])
  const flatItems = useMemo(() => flattenCategories(tree), [tree])
  const selectedId = useMemo(() => {
    if (!value) return undefined
    return flatItems.find((item) => (item.path || item.name) === value)?.id
  }, [flatItems, value])
  const selectedItem = useMemo(
    () => (value ? findCategoryByPath(tree, value) : undefined),
    [tree, value],
  )

  if (loading) {
    return <Spin size="small" />
  }

  if (!tree.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可用文档类型，请在后台维护" />
  }

  return (
    <div className="app-browse-category-tree-wrap">
      <Typography.Text type="secondary" className="app-browse-category-tree-hint">
        展开分类目录，选择最底层文档类型
      </Typography.Text>
      <Tree
        showIcon
        blockNode
        defaultExpandAll
        className="app-browse-category-tree"
        treeData={treeData}
        selectedKeys={selectedId != null ? [selectedId] : []}
        onSelect={(keys, info) => {
          const node = info.node
          if (node.disabled) return
          const id = keys[0]
          if (id == null) return
          const item = flatItems.find((row) => row.id === (typeof id === 'number' ? id : Number(id)))
          if (!item) return
          onChange?.(item.path || item.name)
        }}
      />
      {selectedItem ? (
        <Typography.Text type="secondary" className="app-browse-category-tree-desc">
          {selectedItem.path || selectedItem.name}
          {selectedItem.description ? ` · ${selectedItem.description}` : ''}
        </Typography.Text>
      ) : null}
    </div>
  )
}
